import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkOrders } from '@/stores/workOrderStore';
import { ImpactBadge } from '@/components/ui/status-badges';
import { useElapsedTime } from '@/hooks/useElapsedTime';
import { FinishWorkOrderDialog } from '@/components/work-orders/FinishWorkOrderDialog';
import { MarkBlockedDialog } from '@/components/work-orders/MarkBlockedDialog';
import { toast } from 'sonner';
import type { ClosureData, WorkOrder } from '@/types/maintenance';

interface Props {
  workOrders: WorkOrder[];
}

function WOElapsedClock({ startTime }: { startTime: string | null | undefined }) {
  const elapsed = useElapsedTime(startTime);
  if (!elapsed) return null;
  return (
    <span className="flex items-center gap-0.5 text-[11px] text-molten-deep/70 shrink-0 font-mono tabular-nums">
      <Clock className="h-3 w-3" />
      {elapsed}
    </span>
  );
}

export function MyActiveWork({ workOrders }: Props) {
  const { user } = useAuth();
  const { blockWorkOrder, finishWorkOrder } = useWorkOrders();
  const [finishingWO, setFinishingWO] = useState<WorkOrder | null>(null);
  const [blockingWO, setBlockingWO] = useState<WorkOrder | null>(null);

  // Only show for technicians — managers see the full execution board
  if (!user || user.role === 'manager' || user.role === 'admin' || user.role === 'super_admin') {
    return null;
  }

  const myWOs = workOrders.filter(
    wo => wo.assigned_to === user.id && wo.status === 'in_progress',
  );

  if (myWOs.length === 0) return null;

  async function handleFinish(data: ClosureData) {
    if (!finishingWO) return;
    try {
      await finishWorkOrder(finishingWO.id, data);
      toast.success('Work order closed');
    } catch {
      toast.error('Failed to close work order');
    } finally {
      setFinishingWO(null);
    }
  }

  return (
    <>
      <div className="bg-card rounded-lg border border-border/50">
        <div className="px-4 py-2.5 border-b border-border/50 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-molten shrink-0" />
          <h2 className="text-sm font-semibold">My Active Work</h2>
          <span className="ml-auto text-xs text-muted-foreground">{myWOs.length} in progress</span>
        </div>
        <div className="p-3 space-y-0.5">
          {myWOs.map(wo => (
            <div
              key={wo.id}
              className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-muted/50 transition-colors"
            >
              <Link
                to={`/maintenance/work-orders/${wo.id}`}
                className="flex-1 min-w-0 flex items-center gap-3 group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="font-semibold text-sm">{wo.mold?.mold_id || wo.mold_id}</span>
                    {wo.impact && <ImpactBadge impact={wo.impact} size="sm" />}
                    {wo.machine && (
                      <span className="text-[10px] text-muted-foreground/50">@ {wo.machine.name}</span>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {wo.failure_text || wo.problem_description}
                  </p>
                </div>
                <WOElapsedClock startTime={wo.start_time} />
                <ArrowRight className="h-3 w-3 text-muted-foreground/40 shrink-0" />
              </Link>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-11 text-xs px-3 min-w-[56px]"
                  onClick={() => setFinishingWO(wo)}
                >
                  Finish
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-11 text-xs px-3 min-w-[48px] text-muted-foreground"
                  onClick={() => setBlockingWO(wo)}
                >
                  Block
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {finishingWO && (
        <FinishWorkOrderDialog
          open
          onOpenChange={(open) => { if (!open) setFinishingWO(null); }}
          workOrder={finishingWO}
          onConfirm={handleFinish}
        />
      )}
      <MarkBlockedDialog
        open={!!blockingWO}
        onOpenChange={(open) => { if (!open) setBlockingWO(null); }}
        onConfirm={async (data) => {
          if (!blockingWO) return;
          try {
            await blockWorkOrder(blockingWO.id, data);
            toast.success('Work order blocked');
          } catch {
            toast.error('Failed to block work order');
          } finally {
            setBlockingWO(null);
          }
        }}
      />
    </>
  );
}
